import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Card,
  Chip,
  Stack,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { announcementService } from '../services/announcementService';

const ROLE_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'member', label: 'Member' },
  { value: 'bidder', label: 'Bidder' },
  { value: 'restricted developer', label: 'Restricted Developer' }
];

const CreateAnnouncementDialog = ({ open, onClose, onSuccess, teams = [] }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audienceAll, setAudienceAll] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const resetForm = () => {
    setTitle('');
    setBody('');
    setAudienceAll(true);
    setSelectedRoles([]);
    setSelectedTeams([]);
    setSelectedFiles([]);
    setUploadProgress(0);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleRoleToggle = (roleValue) => {
    setSelectedRoles((prev) =>
      prev.includes(roleValue)
        ? prev.filter((role) => role !== roleValue)
        : [...prev, roleValue]
    );
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      toast.warning('Only image files are allowed');
    }

    setSelectedFiles((prev) => [...prev, ...imageFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    if (!audienceAll && selectedRoles.length === 0 && selectedTeams.length === 0) {
      toast.error('Select at least one role or team');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('body', body.trim());
      formData.append('audienceAll', String(audienceAll));
      formData.append('audienceRoles', JSON.stringify(selectedRoles));
      formData.append('audienceTeamIds', JSON.stringify(selectedTeams.map((team) => team._id)));

      selectedFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      await announcementService.createAnnouncement(formData);
      toast.success('Announcement published');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to publish announcement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        New Announcement
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              fullWidth
              multiline
              minRows={5}
              disabled={loading}
            />

            <Box>
              <FormLabel component="legend" sx={{ mb: 1 }}>Audience</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={audienceAll}
                    onChange={(e) => setAudienceAll(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="All users"
              />

              {!audienceAll && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Select roles and/or teams
                  </Typography>
                  <FormGroup row sx={{ mb: 2 }}>
                    {ROLE_OPTIONS.map((role) => (
                      <FormControlLabel
                        key={role.value}
                        control={
                          <Checkbox
                            checked={selectedRoles.includes(role.value)}
                            onChange={() => handleRoleToggle(role.value)}
                            disabled={loading}
                          />
                        }
                        label={role.label}
                      />
                    ))}
                  </FormGroup>

                  <Autocomplete
                    multiple
                    options={teams}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedTeams}
                    onChange={(_, value) => setSelectedTeams(value)}
                    disabled={loading}
                    renderInput={(params) => (
                      <TextField {...params} label="Teams" placeholder="Select teams" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option._id}
                          label={option.name}
                          size="small"
                        />
                      ))
                    }
                  />
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Screenshots (optional, max 5)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={loading || selectedFiles.length >= 5}
              >
                Upload images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFiles.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {selectedFiles.map((file, index) => (
                    <Card key={`${file.name}-${index}`} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ImageIcon color="action" fontSize="small" />
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                        {file.name}
                      </Typography>
                      <IconButton size="small" onClick={() => removeFile(index)} disabled={loading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {loading && uploadProgress > 0 && (
              <Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Publishing...' : 'Publish'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateAnnouncementDialog;
